/* eslint-disable consistent-return */
import { Box, Hidden, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import i18n from '@utils/i18n';

import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';

import useResponsive from '@hooks/useResponsive';
import env from '@constants/env';

const BannerImg = `${env.s3BaseUrl}/landing-page/homepage/landing-banner.png`;
const BannerImgMobile = `${env.s3BaseUrl}/landing-page/homepage/mobile-banner.png`;
const BannerImgTablet = `${env.s3BaseUrl}/landing-page/homepage/banner-tablet.png`;
const ItalicText = `${env.s3BaseUrl}/landing-page/homepage/italic-text.png`;
const ItalicTextMobile = `${env.s3BaseUrl}/landing-page/homepage/italic-mobile.png`;
const line = `${env.s3BaseUrl}/landing-page/homepage/line.png`;

// Define the keyframes inside a style tag in your component or in your CSS file
const styles = `
  @keyframes backgroundScroll {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: 100% 0%;
    }
  }

  @keyframes fade-up {
    0% {
      opacity: 0;
      transform: translateY(0px);
    }
    100% {
      opacity: 1;
      transform: translateY(-40px);
    }
  }
`;

const isEnglish = () => {
  const lang = localStorage.getItem('i18nextLng')?.toLowerCase() === 'en';
  return lang;
};

gsap.registerPlugin(ScrollTrigger);
const Banner = () => {
  const bannerT = i18n.getFixedT(null, null, 'pages.flagshop.banner');
  const t = i18n.getFixedT(null, null, '');
  const isSm = useResponsive('down', 1000);
  const isTablet = useResponsive('between', 0, 500, 1000);
  const backdropRef = useRef(null);
  const componentRef = useRef(null);
  useEffect(() => {
    // Register the ScrollTrigger plugin

    // Ensure refs are available
    if (componentRef.current && backdropRef.current && !isSm) {
      // Create timeline with scroll trigger
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: componentRef.current,
          start: 'top top',
          end: '+=100%',

          scrub: 1, // This is essential for scroll-based animation
          markers: false,
          id: 'banner-blur-animation'
        }
      });

      // Add animation to timeline
      timeline.to(componentRef.current, {
        opacity: 0
      });

      return () => {
        if (timeline.scrollTrigger) {
          timeline.scrollTrigger.kill();
        }
      };
    }
  }, []);
  return (
    <>
      <style>{styles}</style>
      <Stack
        sx={{
          width: '100%',
          height: { md: '100vh', xs: '100svh' },
          mt: { md: '-54px', xs: '-54px' },

          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          // backgroundImage: {
          //   xs: `url(${BannerImgMobile})`,
          //   sm: `url(${BannerImg})`,
          //   md: `url(${BannerImg})`
          // },
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: isSm ? '100% 100%' : 'cover',
          zIndex: 0
        }}
        ref={componentRef}
      >
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            display: { xs: 'none', md: 'flex' }
          }}
        >
          <Box
            component="img"
            ref={backdropRef}
            src={BannerImg}
            loading="eager"
            alt="store in office"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              width: '100%',
              height: '100%',
              bottom: 0,
              zIndex: 1
            }}
          />
          <Stack
            sx={{
              flexDirection: {
                md: 'column',
                sm: 'column'
              },

              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { md: 5, xs: 5 },
              mt: { md: '64px', xs: '54px' },
              zIndex: 3
            }}
          >
            {/* banner title */}
            <Stack
              sx={{
                gap: { md: 2.5, xs: 1 },
                width: '100%',
                px: { xs: 2.5, md: 0 },
                textAlign: { md: 'center', xs: 'left' },
                animation: 'fade-up 300ms ease-in forwards'
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: '48px !important',
                  color: '#FFF',
                  fontWeight: 700,
                  letterSpacing: '-2.4px',
                  lineHeight: '150%',
                  whiteSpace: 'pre-line'
                }}
              >
                {bannerT('title')}
              </Typography>
            </Stack>

            <Stack
              sx={{
                flexDirection: 'row',

                alignItems: 'center',
                width: '100%',
                maxWidth: 'fit-content',
                animation: 'fade-up 300ms ease-in forwards'
              }}
            >
              <img src={ItalicText} alt="experience your high" />
              <Stack
                sx={{
                  flexGrow: 1,
                  ml: '7px',
                  height: '1px',
                  minWidth: '214px',
                  backgroundColor: '#FFFFFF'
                }}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="26"
                viewBox="0 0 28 26"
                style={{
                  marginLeft: '-7px',

                  marginBottom: '1px'
                }}
                fill="none"
              >
                <g clipPath="url(#clip0_210_5854)">
                  <path
                    d="M15.807 14.7349L19.8883 23.0788L14.4379 15.5321L12.0712 24.5372L12.868 15.2805L5.1657 20.7369L11.8388 14.101L2.40117 13.4527L11.8307 12.5458L5.07116 6.09277L12.8441 11.3359L11.9298 2.10478L14.4065 11.0435L19.7636 3.35223L15.7888 11.8051L24.9099 9.24957L16.3401 13.2631L24.9619 17.0413L15.807 14.7349Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_210_5854">
                    <rect
                      width="22.8978"
                      height="22.08"
                      fill="white"
                      transform="translate(0.671875 4.21143) rotate(-10.599)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </Stack>
          </Stack>
        </Stack>
        <Hidden mdUp>
          <Box
            component="img"
            src={isTablet ? BannerImgTablet : BannerImgMobile}
            loading="eager"
            alt="store in office"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'bottom',
              bottom: 0,
              zIndex: 1
            }}
          />
          <Stack
            sx={{
              flexDirection: {
                md: 'column',
                sm: 'column'
              },
              width: '100%',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { md: 5, xs: 5 },
              mt: { md: '64px', xs: '54px' },
              zIndex: 3
            }}
          >
            <Stack
              sx={{
                width: '100%',
                px: { xs: 2.5, sm: 5, md: 0 },

                justifyContent: { sm: 'center' },
                alignItems: { sm: !isEnglish() ? 'center' : 'flex-start' }
              }}
            >
              <Stack
                sx={{
                  gap: { md: 2.5, xs: 1 },

                  textAlign: {
                    md: 'center',
                    xs: 'left',
                    sm: !isEnglish() ? 'center' : 'left'
                  },
                  alignItems: !isEnglish() ? 'center' : 'flex-start'
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '30px !important', sm: '40px !important' },
                    color: '#FFF',
                    fontWeight: 700,
                    letterSpacing: { xs: '-1.5px', sm: '-2px' },
                    textAlign: 'left',
                    lineHeight: '130%'
                  }}
                >
                  {bannerT('title')}
                </Typography>

                <Stack
                  sx={{
                    flexDirection: 'row',
                    maxWidth: 'fit-content',
                    mt: { sm: 2 },
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <img
                    src={ItalicTextMobile}
                    alt="experience your high"
                    style={{
                      width: 'auto',
                      height: '24px',
                      marginRight: '4px'
                    }}
                  />

                  <img
                    src={line}
                    alt="experience your high"
                    style={{ flexGrow: 1, height: '25px' }}
                  />
                </Stack>
              </Stack>
            </Stack>
            {/* banner title */}
          </Stack>
        </Hidden>
      </Stack>
    </>
  );
};

export default Banner;
